import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Plus, Pencil, Trash2, X, ArrowLeft,
  ChevronLeft, ChevronRight, FileText, Clock, Users,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import SortableHeader, { PAGE_SIZE } from '../components/SortableTable';
import ResultCount from '../components/shared/ResultCount';
import { fetchJSON } from '../utils/api';
import { Field, inputCls, lockedInputCls, LockTip } from '../components/shared/FormKit';
import { FormValidationProvider, useFormValidation } from '../components/shared/formValidation';

const THEAD_CLS = 'px-4 py-2 text-xs font-semibold text-slate-500 uppercase';

// Record type configuration — one page, two record resources of the same shape
// (Training hours moved to the L&D module in Phase 6 — see /ld/records.)
const RECORD_TABS = [
  {
    key: 'timesheet',
    label: 'Timesheet Marks',
    icon: FileText,
    valueFields: [{ key: 'marksDeducted', label: 'Marks Deducted' }],
  },
  {
    key: 'chargeable',
    label: 'Chargeable Hours',
    icon: Clock,
    valueFields: [
      { key: 'auditHours', label: 'Audit Hours' },
      { key: 'nonAuditHours', label: 'Non-audit Hours' },
    ],
  },
];

const getTabConfig = (key) => RECORD_TABS.find(t => t.key === key) || RECORD_TABS[0];

// Month values arrive as YYYY-MM (or longer ISO strings) — keep YYYY-MM only.
const toMonthInput = (v) => (v ? String(v).slice(0, 7) : '');

// Bucket the staff feed into designation <optgroup>s. The feed arrives in
// designation-seniority order, so first-appearance order is the display order.
const groupByPosition = (staff) => {
  const order = [];
  const byGroup = new Map();
  (staff || []).forEach((s) => {
    const g = s.position || 'No designation';
    if (!byGroup.has(g)) { byGroup.set(g, []); order.push(g); }
    byGroup.get(g).push(s);
  });
  return order.map((g) => ({ group: g, items: byGroup.get(g) }));
};

const QMMonthlyRecords = () => {
  const { hasPermission } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const rawTab = searchParams.get('tab');
  const activeTab = RECORD_TABS.some(t => t.key === rawTab) ? rawTab : 'timesheet';
  const tabConfig = getTabConfig(activeTab);

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [monthFilter, setMonthFilter] = useState('');
  const [staffFilter, setStaffFilter] = useState('');
  const [staffOptions, setStaffOptions] = useState([]);
  const [sortConfig, setSortConfig] = useState(null);

  const requestSort = (key) => {
    setPage(1);
    setSortConfig(prev =>
      prev && prev.key === key
        ? { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
        : { key, direction: 'asc' });
  };

  const [editing, setEditing] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [deleting, setDeleting] = useState(null);

  const canEdit = hasPermission('qm:monthly:edit');

  const switchTab = (key) => {
    setSearchParams({ tab: key });
    setPage(1);
    setSortConfig(null);
  };

  // Reset to first page whenever a filter or the tab changes
  useEffect(() => {
    setPage(1);
  }, [monthFilter, staffFilter, activeTab]);

  // Active staff for the filter dropdown and the record modal
  const loadStaffOptions = useCallback(async () => {
    try {
      const data = await fetchJSON('/api/qm/staff/options?active=true');
      setStaffOptions(data.staff || []);
    } catch (e) {
      console.error('Error loading staff options:', e);
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (monthFilter) params.set('month', monthFilter);
      if (staffFilter) params.set('staffId', staffFilter);
      if (sortConfig?.key) { params.set('sort', sortConfig.key); params.set('dir', sortConfig.direction); }
      params.set('page', String(page));
      params.set('pageSize', String(PAGE_SIZE));
      const data = await fetchJSON(`/api/qm/records/${activeTab}?${params}`);
      setRows(data.data || []);
      setTotal(data.total || 0);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [activeTab, monthFilter, staffFilter, sortConfig, page]);

  useEffect(() => {
    if (!canEdit) return;
    loadStaffOptions();
  }, [canEdit, loadStaffOptions]);

  useEffect(() => {
    if (!canEdit) return;
    load();
  }, [canEdit, load]);

  if (!canEdit) {
    return (
      <div className="p-8 max-w-xl mx-auto">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <h2 className="text-lg font-semibold text-red-800">Editor access required</h2>
          <p className="text-sm text-red-600 mt-1">You need staff record editing rights to access this area.</p>
        </div>
      </div>
    );
  }

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto">
      <Link to="/manage" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700"><ArrowLeft className="w-4 h-4" />Management</Link>
      <div className="mt-2 mb-4 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900">Monthly Records</h1>
          <p className="text-sm text-slate-500 mt-1">Manage the monthly data feeding the Timesheets and Chargeable Hours views.</p>
        </div>
        <div className="flex gap-2">
          <Link
            to="/qm/staff"
            className="inline-flex items-center justify-center gap-2 px-3 py-2 bg-white border border-slate-300 text-slate-700 text-sm font-medium rounded-lg hover:bg-slate-50"
          >
            <Users className="w-4 h-4" /> Manage Staff
          </Link>
          <button
            onClick={() => setShowCreate(true)}
            className="inline-flex items-center justify-center gap-2 px-3 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700"
          >
            <Plus className="w-4 h-4" /> New Record
          </button>
        </div>
      </div>

      {/* Sub-tab bar */}
      <div className="bg-white border-b border-slate-200 px-4 mb-4 rounded-t-lg border border-b-slate-200">
        <div className="flex gap-6 overflow-x-auto">
          {RECORD_TABS.map(t => {
            const Icon = t.icon;
            return (
              <button
                key={t.key}
                onClick={() => switchTab(t.key)}
                className={`py-3 text-sm font-medium transition-colors flex items-center gap-2 ${activeTab === t.key ? 'text-blue-600 border-b-2 border-blue-600' : 'text-slate-500 border-b-2 border-transparent hover:text-slate-700 hover:border-slate-200'}`}
              >
                <Icon className="w-4 h-4" /> {t.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Toolbar */}
      <div className="bg-white rounded-lg border border-slate-200 p-3 mb-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <label className="text-xs text-slate-500 font-medium whitespace-nowrap">Month</label>
            <input
              type="month"
              value={monthFilter}
              onChange={e => setMonthFilter(e.target.value)}
              className={`${inputCls} sm:w-auto`}
            />
            {monthFilter && (
              <button onClick={() => setMonthFilter('')} className="text-sm text-blue-600 hover:text-blue-800">Clear</button>
            )}
          </div>
          <select value={staffFilter} onChange={e => setStaffFilter(e.target.value)} className={`${inputCls} sm:w-auto`}>
            <option value="">All staff</option>
            {groupByPosition(staffOptions).map(g => (
              <optgroup key={g.group} label={g.group}>
                {g.items.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </optgroup>
            ))}
          </select>
          <ResultCount count={total} loading={loading} className="ml-auto" />
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 text-sm mb-4">{error}</div>
      )}

      {/* Table */}
      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
        <div className="table-scroll">
          <table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead className="bg-slate-50">
              <tr>
                <SortableHeader label="Staff" sortKey="staffName" sortConfig={sortConfig} onSort={requestSort} className={THEAD_CLS} />
                <SortableHeader label="Designation" sortKey="position" sortConfig={sortConfig} onSort={requestSort} className={THEAD_CLS} />
                <SortableHeader label="Month" sortKey="month" sortConfig={sortConfig} onSort={requestSort} className={THEAD_CLS} />
                {tabConfig.valueFields.map(f => (
                  <SortableHeader key={f.key} label={f.label} sortKey={f.key} align="right" sortConfig={sortConfig} onSort={requestSort} className={THEAD_CLS} />
                ))}
                <SortableHeader label="Remarks" sortKey="remarks" sortConfig={sortConfig} onSort={requestSort} className={THEAD_CLS} />
                <SortableHeader label="Actions" sortable={false} align="right" className={THEAD_CLS} />
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-slate-100">
              {loading ? (
                <tr>
                  <td colSpan={5 + tabConfig.valueFields.length} className="px-4 py-8 text-center text-slate-500">Loading…</td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={5 + tabConfig.valueFields.length} className="px-4 py-8 text-center text-slate-500">No records found</td>
                </tr>
              ) : rows.map(r => (
                <tr key={r.id} className="hover:bg-slate-50">
                  <td className="px-4 py-2 text-slate-900">{r.staffName}</td>
                  <td className="px-4 py-2 text-slate-700">{r.position || '—'}</td>
                  <td className="px-4 py-2 text-slate-700 whitespace-nowrap">{toMonthInput(r.month)}</td>
                  {tabConfig.valueFields.map(f => (
                    <td key={f.key} className="px-4 py-2 text-right text-slate-700">{r[f.key] ?? '—'}</td>
                  ))}
                  <td className="px-4 py-2 text-slate-700 max-w-[260px] truncate" title={r.remarks || ''}>{r.remarks || '—'}</td>
                  <td className="px-4 py-2 text-right">
                    <div className="inline-flex gap-1">
                      <button
                        onClick={() => setEditing(r)}
                        title="Edit"
                        className="p-1.5 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setDeleting(r)}
                        title="Delete"
                        className="p-1.5 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {/* Pagination */}
        <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200 bg-slate-50 text-sm">
          <span className="text-slate-500">
            {total > 0
              ? `${(page - 1) * PAGE_SIZE + 1}–${Math.min(page * PAGE_SIZE, total)} of ${total}`
              : '0 of 0'}
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="inline-flex items-center gap-1 px-2.5 py-1.5 border border-slate-300 rounded-md text-slate-600 hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-4 h-4" /> Prev
            </button>
            <span className="text-slate-500">Page {page} of {totalPages}</span>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              className="inline-flex items-center gap-1 px-2.5 py-1.5 border border-slate-300 rounded-md text-slate-600 hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Next <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {editing && (
        <RecordModal
          type={activeTab}
          tabConfig={tabConfig}
          record={editing}
          staffOptions={staffOptions}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
      {showCreate && (
        <RecordModal
          type={activeTab}
          tabConfig={tabConfig}
          record={null}
          staffOptions={staffOptions}
          onClose={() => setShowCreate(false)}
          onSaved={() => { setShowCreate(false); load(); }}
        />
      )}
      {deleting && (
        <DeleteRecordModal
          type={activeTab}
          tabConfig={tabConfig}
          record={deleting}
          onClose={() => setDeleting(null)}
          onDeleted={() => { setDeleting(null); load(); }}
        />
      )}
    </div>
  );
};

const RecordModal = ({ type, tabConfig, record, staffOptions, onClose, onSaved }) => {
  const isEdit = !!record;

  const initialValues = {};
  tabConfig.valueFields.forEach(f => {
    initialValues[f.key] = record?.[f.key] ?? '';
  });

  const [form, setForm] = useState({
    staffId: record?.staffId ? String(record.staffId) : '',
    month: toMonthInput(record?.month),
    remarks: record?.remarks || '',
    ...initialValues,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const setField = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  // 空白的必填格：按 Save 後就地標紅並列進橫幅，填好就消。
  const gaps = useMemo(() => {
    const g = [];
    if (!isEdit && !form.staffId) g.push({ key: 'staffId', label: 'Staff' });
    if (!form.month) g.push({ key: 'month', label: 'Month' });
    tabConfig.valueFields.forEach(f => {
      if (form[f.key] === '' || form[f.key] === null) g.push({ key: f.key, label: f.label });
    });
    return g;
  }, [form, isEdit, tabConfig]);
  const validation = useFormValidation(gaps);

  const save = async () => {
    if (!validation.validate()) { setError(null); return; }
    for (const f of tabConfig.valueFields) {
      if (Number(form[f.key]) < 0) {
        setError(`${f.label} cannot be negative.`);
        return;
      }
    }

    setSaving(true);
    setError(null);
    const values = {};
    tabConfig.valueFields.forEach(f => {
      values[f.key] = Number(form[f.key]);
    });
    try {
      if (isEdit) {
        await fetchJSON(`/api/qm/records/${type}/${record.id}`, {
          method: 'PATCH',
          body: JSON.stringify({
            month: form.month,
            ...values,
            remarks: form.remarks.trim() || null,
          }),
        });
      } else {
        await fetchJSON(`/api/qm/records/${type}`, {
          method: 'POST',
          body: JSON.stringify({
            staffId: Number(form.staffId),
            month: form.month,
            ...values,
            remarks: form.remarks.trim() || null,
          }),
        });
      }
      onSaved();
    } catch (e) {
      if (e.code === 'DUPLICATE_RECORD') {
        setError('A record for this staff and month already exists — edit it instead.');
      } else {
        setError(e.message);
      }
    }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-slate-200">
          <h3 className="text-lg font-semibold text-slate-900">
            {isEdit ? `Edit Record — ${record.staffName}` : `New ${tabConfig.label} Record`}
          </h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-6 space-y-4">
          <FormValidationProvider value={validation.providerValue}>
          <Field label="Staff" required field="staffId">
            {isEdit ? (
              <LockTip text="Fixed once the record is saved — delete it and add a new one for another person" block>
                <input type="text" value={record.staffName} disabled className={lockedInputCls} />
              </LockTip>
            ) : (
              <select value={form.staffId} onChange={e => setField('staffId', e.target.value)} className={inputCls}>
                <option value="">Select…</option>
                {groupByPosition(staffOptions).map(g => (
                  <optgroup key={g.group} label={g.group}>
                    {g.items.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </optgroup>
                ))}
              </select>
            )}
          </Field>
          <Field label="Month" required field="month">
            <input type="month" value={form.month} onChange={e => setField('month', e.target.value)} className={inputCls} />
          </Field>
          {tabConfig.valueFields.map(f => (
            <Field key={f.key} label={f.label} required field={f.key}>
              <input
                type="number"
                min="0"
                step="0.5"
                value={form[f.key]}
                onChange={e => setField(f.key, e.target.value)}
                className={inputCls}
              />
            </Field>
          ))}
          <Field label="Remarks">
            <textarea rows="2" value={form.remarks} onChange={e => setField('remarks', e.target.value)} className={inputCls} />
          </Field>
          {(error || validation.error) && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              <span>{error || validation.error}</span>
            </div>
          )}
          </FormValidationProvider>
          <div className="flex justify-end gap-2 pt-2">
            <button onClick={onClose} className="px-3 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded">Cancel</button>
            <button onClick={save} disabled={saving} className="px-3 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:opacity-50">
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const DeleteRecordModal = ({ type, tabConfig, record, onClose, onDeleted }) => {
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState(null);

  const confirmDelete = async () => {
    setDeleting(true);
    setError(null);
    try {
      await fetchJSON(`/api/qm/records/${type}/${record.id}`, { method: 'DELETE' });
      onDeleted();
    } catch (e) { setError(e.message); }
    finally { setDeleting(false); }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        <div className="flex items-center justify-between p-6 border-b border-slate-200">
          <h3 className="text-lg font-semibold text-slate-900">Delete Record</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-6 space-y-4">
          <p className="text-sm text-slate-700">
            Are you sure you want to delete the {tabConfig.label.toLowerCase()} record for{' '}
            <span className="font-semibold">{record.staffName}</span>
            {record.month ? ` (${toMonthInput(record.month)})` : ''}? This action cannot be undone.
          </p>
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              <span>{error}</span>
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <button onClick={onClose} className="px-3 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded">Cancel</button>
            <button onClick={confirmDelete} disabled={deleting} className="px-3 py-2 bg-red-600 text-white text-sm rounded hover:bg-red-700 disabled:opacity-50">
              {deleting ? 'Deleting…' : 'Delete'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QMMonthlyRecords;
